"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SpeedRoomScene = void 0;
const Scene_1 = require("./Scene");
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const Global_1 = require("../Global");
const ui_1 = require("@pixi/ui");
const interfaces_1 = require("../../../shared/interfaces");
const blocks_1 = require("../blocks");
const i18next_1 = __importDefault(require("i18next"));
const SpeedSprite_1 = require("../objects/SpeedSprite");
const gsap_1 = require("gsap");
const WigglyText_1 = require("../objects/WigglyText");
const settings_1 = require("../config/settings");
class SpeedRoomScene extends Scene_1.Scene {
    constructor(app, sceneManager, gameManager) {
        super(app, sceneManager, gameManager, settings_1.Settings.speedColor);
        this.notifydelay = 1.7;
        this.notified = false;
        this.updateText = this.makeUpdateText();
        this.updateText.visible = false;
        this.footerText = this.makeFooterText('typeinfo.' + interfaces_1.GameType.Speed);
        this.footerText.alpha = 0;
        this.footerText.position.y = this.app.screen.height;
        this.labelText = this.makeLabelText();
        this.speedSprite = this.makeSpeedSprite();
        this.subs.add(this.gameManager.room$.subscribe({
            next: (room) => __awaiter(this, void 0, void 0, function* () {
                if (!this.notified && room && room.type === interfaces_1.GameType.Speed && !this.isDeinited) {
                    this.notified = true;
                    this.recordText = this.makeRecordText(room);
                    this.recordText.visible = false;
                    this.layout();
                    yield (0, Global_1.fade)(this.recordText, 1, 0.5);
                    yield (0, Global_1.fade)(this.updateText, 1, 0.5);
                    yield (0, Global_1.sleep)(this.notifydelay);
                    if (!this.isDeinited) {
                        yield this.promisedTimeline(gsap_1.gsap.timeline().to(this.speedSprite, {
                            pixi: {
                                positionX: (0, Global_1.isrtl)() ? -this.speedSprite.width / 2 : this.app.screen.width + this.speedSprite.width / 2
                            },
                            ease: 'power2.in',
                            duration: 0.6
                        }));
                        this.gameManager.notifyReady();
                    }
                }
            })
        }));
    }
    animate() {
        return __awaiter(this, void 0, void 0, function* () {
            const tm = this.promisedTimeline(gsap_1.gsap.timeline({
                autoRemoveChildren: true
            }).to(this.speedSprite, {
                pixi: {
                    positionX: this.center.x
                },
                ease: 'power2.out',
                duration: 0.6
            }));
            yield Promise.all([this.animateFooterText(this.footerText), tm]);
        });
    }
    init() {
        const _super = Object.create(null, {
            init: { get: () => super.init }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.init.call(this);
            yield this.animate();
            this.gameManager.enterRoom(interfaces_1.GameType.Speed);
        });
    }
    exitScene(to, target) {
        const _super = Object.create(null, {
            exitScene: { get: () => super.exitScene }
        });
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.zIndex = SceneManager_1.Layer.Top;
            // Find our button
            const mybutton = (_a = to.getChildByName('game' + interfaces_1.GameType.Speed, true)) !== null && _a !== void 0 ? _a : to.getChildByName('others', true);
            if (!this.crash && mybutton instanceof ui_1.ButtonContainer) {
                yield this.animateMask(mybutton, true);
            }
            else if (this.crash) {
                yield this.animateFall();
            }
            return _super.exitScene.call(this, to);
        });
    }
    enterScene(from, target) {
        const _super = Object.create(null, {
            enterScene: { get: () => super.enterScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            // Have a cool zoom animation
            if (target instanceof ui_1.ButtonContainer) {
                yield this.animateMask(target);
            }
            return _super.enterScene.call(this, from);
        });
    }
    makeSpeedSprite() {
        const sprite = new SpeedSprite_1.SpeedSprite(this.app);
        const sp = (0, Global_1.isrtl)() ? this.app.screen.width + sprite.width / 2 : -sprite.width / 2;
        sprite.position.set(sp, this.speedYSpot);
        if ((0, Global_1.isrtl)()) {
            sprite.scale.x *= -1;
        }
        sprite.play();
        return this.addChild(sprite);
    }
    makeUpdateText() {
        const now = new Date();
        const hourstill = 24 - now.getUTCHours();
        const style = blocks_1.Block.textStyle(this.overSmallSize ? 24 : 22);
        style.align = 'center';
        style.fill = settings_1.Settings.grayColor;
        const text = new WigglyText_1.WigglyText(this.app, i18next_1.default.t('speed.updated', { count: hourstill }), style);
        text.anchor.set(0.5, 1);
        return this.addChild(text);
    }
    makeLabelText() {
        const container = new pixi_js_1.Container();
        const style = blocks_1.Block.textStyle(this.overSmallSize ? 52 : 40);
        style.fill = settings_1.Settings.grayColor;
        style.align = 'center';
        style.padding = (0, Global_1.px)(10);
        style.wordWrap = true;
        style.wordWrapWidth = this.app.screen.width - (0, Global_1.px)(80);
        this.label = new WigglyText_1.WigglyText(this.app, i18next_1.default.t('speed.today'), style);
        this.label.power = (0, Global_1.px)(7);
        this.label.anchor.set(0.5, 1);
        container.addChild(this.label);
        return this.addChild(container);
    }
    makeRecordText(room) {
        var _a;
        const container = new pixi_js_1.Container();
        let txt;
        if ((_a = room.record) === null || _a === void 0 ? void 0 : _a.u) {
            txt = i18next_1.default.t('speed.record', { val: (0, Global_1.timed)(room.record.score), person: room.record.u,
                interpolation: { escapeValue: false } });
        }
        else {
            txt = i18next_1.default.t('speed.norecord');
        }
        const style = blocks_1.Block.textStyle(this.overSmallSize ? 52 : 40);
        style.align = 'center';
        style.lineHeight = (0, Global_1.px)(60);
        style.wordWrap = true;
        style.wordWrapWidth = this.app.screen.width;
        this.record = new pixi_js_1.Text(txt, style);
        this.record.anchor.set(0.5, 0);
        container.addChild(this.record);
        return this.addChild(container);
    }
    get speedYSpot() {
        return this.app.screen.height - (0, Global_1.px)(this.isLandscapeBox ? 80 : 150);
    }
    get triggersStart() {
        return true;
    }
    destroy(_options) {
        this.label.destroy(_options);
        super.destroy(_options);
    }
    layout() {
        var _a, _b, _c;
        super.layout();
        const centerx = this.center.x;
        if (this.isInited) {
            this.footerText.position.y = this.app.screen.height - this.footerText.height + (0, Global_1.px)(15);
            this.speedSprite.position.set(centerx, this.speedYSpot);
        }
        this.label.style.fontSize = (0, Global_1.fontSize)(this.overSmallSize ? 52 : 40);
        this.label.style.wordWrapWidth = this.app.screen.width - (0, Global_1.px)(80);
        this.labelText.position.set(centerx, this.isLandscapeBox ? (0, Global_1.px)(60) : this.app.screen.height / 3);
        if (this.record) {
            this.record.style.fontSize = (0, Global_1.fontSize)(this.overSmallSize ? 52 : 40);
            this.record.style.wordWrapWidth = this.app.screen.width;
        }
        (_a = this.recordText) === null || _a === void 0 ? void 0 : _a.position.set(centerx, this.isLandscapeBox ? this.labelText.height + (0, Global_1.px)(20) : this.app.screen.height / 3 + (0, Global_1.px)(this.overSmallSize ? 20 : 14));
        if (this.updateText) {
            this.updateText.style.fontSize = (0, Global_1.fontSize)(this.overSmallSize ? 24 : 22);
            this.updateText.position.set(centerx, ((_b = this.recordText) === null || _b === void 0 ? void 0 : _b.y) + ((_c = this.recordText) === null || _c === void 0 ? void 0 : _c.height) + (0, Global_1.px)(this.overSmallSize ? 30 : 15));
        }
        this.footerText.position.x = centerx;
    }
}
exports.SpeedRoomScene = SpeedRoomScene;
